"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * BCP-47 Tag Format:
 * A tag is fully specifed as lll-Ssss-RRR [lll = language, Ssss = Script, RRR = Region]
 * eg: en-Latn-US, zh-Hant-TW, es-Latn-419
 *
 * Language Tag: https://www.w3.org/International/articles/language-tags/
 * Formatted as lll[-Ssss][-RRR]. Abbreviated to the shortest code that makes a language uniquely identifiable. For eg,
 * 1. ja = ja-JP = ja-Jpan-JP = Japanese (lll)
 * 2. en = en-US = en-Latn-US = English (lll)
 * 3. en-GB = en-Latn-GB = English (UK) (lll-RRR)
 * 4. zh = zh-Hans = zh-CN = zh-Hans-CN = Chinese (Simplified/Chinese) (lll)
 * 5. zh-Hant = zh-TW = zh-Hant-TW = Chinese (Traditional/Taiwanese) (lll-Ssss or lll-RRR)
 * The shortest code often refers to the region with the largest population of speakers for
 * a certain language, which is not necessarily the origin region of the language.
 * (eg: en refers to en-US and not en-GB). As a result, shortest codes are "living" and can ocassionally
 * be remapped by a standards body (as in the case of pt = pt-BR instead of pt-PT)
 *
 * Locale Tag:
 * A locale tag is always of the format lll-RRR
 *
 * Deprecation of tags:
 * The below supportedLocales mapping is obtained from the Go team as per the latest standards:
 * 1. The two Serbian versions were previously reported as sr for Serbian (Cyrillic) and
 * sh for Serbian (Latin). However, sh is now deprecated in favor of sr-Latn.
 * 2. zh is now a "macrolanguage", meaning mapping zh for Simplified Chinese is now deprecated
 * and should now be specified as either zh-CN or zh-Hans
 * 3. pt now refers to pt-BR and not pt-PT.
 * However, for backwards compatibility, browsers still report langage tags using older standards.
 */
var supportedLocales = [
    { name: "Czech", endonym: "Česky", localeTag: "cs-CZ", languageTag: "cs" },
    { name: "Danish", endonym: "Dansk", localeTag: "da-DK", languageTag: "da" },
    { name: "German", endonym: "Deutsch", localeTag: "de-DE", languageTag: "de" },
    { name: "English", endonym: "English", localeTag: "en-US", languageTag: "en" },
    { name: "Spanish", endonym: "Español", localeTag: "es-ES", languageTag: "es" },
    { name: "French", endonym: "Français", localeTag: "fr-FR", languageTag: "fr" },
    { name: "French (Canadian)", endonym: "Français (Canada)", localeTag: "fr-CA", languageTag: "fr-CA" },
    { name: "Croatian", endonym: "hrvatski", localeTag: "hr-HR", languageTag: "hr" },
    { name: "Italian", endonym: "Italiano", localeTag: "it-IT", languageTag: "it" },
    { name: "Hungarian", endonym: "magyar", localeTag: "hu-HU", languageTag: "hu" },
    { name: "Dutch", endonym: "Nederlands", localeTag: "nl-NL", languageTag: "nl" },
    { name: "Norwegian", endonym: "Norsk", localeTag: "no-NO", languageTag: "no" },
    { name: "Polish", endonym: "Polski", localeTag: "pl-PL", languageTag: "pl" },
    { name: "Portuguese (Brazilian)", endonym: "Português (Brasil)", localeTag: "pt-BR", languageTag: "pt" },
    { name: "Portuguese", endonym: "Português", localeTag: "pt-PT", languageTag: "pt-PT", deprecatedLanguageTag: "pt" },
    { name: "Romanian", endonym: "Română", localeTag: "ro-RO", languageTag: "ro" },
    { name: "Slovak", endonym: "slovenčina", localeTag: "sk-SK", languageTag: "sk" },
    { name: "Slovenian", endonym: "slovenščina", localeTag: "sl-SI", languageTag: "sl" },
    { name: "Serbian (Latin)", endonym: "srpski", localeTag: "sh-RS", languageTag: "sr-Latn",
        deprecatedLanguageTag: "sh" },
    { name: "Finnish", endonym: "suomi", localeTag: "fi-FI", languageTag: "fi" },
    { name: "Swedish", endonym: "Svenska", localeTag: "sv-SE", languageTag: "sv" },
    { name: "Turkish", endonym: "Türkçe", localeTag: "tr-TR", languageTag: "tr" },
    { name: "Greek", endonym: "Ελληνικά", localeTag: "el-GR", languageTag: "el" },
    { name: "Russian", endonym: "Русский", localeTag: "ru-RU", languageTag: "ru" },
    { name: "Serbian", endonym: "српски", localeTag: "sr-RS", languageTag: "sr" },
    { name: "Thai", endonym: "ไทย", localeTag: "th-TH", languageTag: "th" },
    { name: "Korean", endonym: "한국어", localeTag: "ko-KR", languageTag: "ko" },
    { name: "Chinese (Simplified)", endonym: "中文 (简体)", localeTag: "zh-CN", languageTag: "zh-Hans",
        deprecatedLanguageTag: "zh" },
    { name: "Chinese (Traditional)", endonym: "中文 (繁體)", localeTag: "zh-TW", languageTag: "zh-Hant" },
    { name: "Japanese", endonym: "日本語", localeTag: "ja-JP", languageTag: "ja" },
];
/**
 * Heuristically find the locale from a locale or language tag
 *
 * Given an input tag of the form lll[-Ssss][-RRR], search supportedLocales for an exact match in this order:
 * 1. [deprecated langauge tag]
 * 2. language tag
 * 3. locale tag
 * 4. [reduce input to lll and search again in deprecated language tag]
 * 5. reduce input to lll and search again in language tag
 *
 * Example 1:
 * zh does not match anything
 * zh matches Chinese (simplified) in step 1 if deprecated tags are allowed
 * zh-Hans matches Chinese (simplified) in step 2
 * zh-CN matches Chinese (simplified) in step 3
 *
 * Example 2:
 * pt matches Portuguese in step 1 if deprecated tags are allowed
 * pt matches Portuguese (Brazilian) in step 2 otherwise
 *
 * Eample 3:
 * en-GB is stripped to en and matches English in step 5
 *
 * @param localeOrLanguageTag Locale or language tag of the form lll[-Ssss][-RRR]
 * @param useDeprecatedLanguageTag Search older codes, for example codes from navigator.languages?
 */
function findSupportedLocale(localeOrLanguageTag, useDeprecatedLanguageTag) {
    if (useDeprecatedLanguageTag === void 0) { useDeprecatedLanguageTag = false; }
    if (!localeOrLanguageTag) {
        return;
    }
    var tag = localeOrLanguageTag.replace(/_/g, "-");
    // validate the tag is of format lll[-Ssss][-rrr]
    // language code and region code is 2 or 3 characrters long
    // script code is always 4 characters long
    // all codes can be alpha-numeric
    // script code and region code are optional
    if (!tag.match(/^\w{2,3}(-\w{4})?(-\w{2,3})?$/)) {
        return;
    }
    var locale = findSupportedLocaleHelper(tag, useDeprecatedLanguageTag);
    if (locale) {
        return locale;
    }
    var tagSplit = tag.split("-");
    if (tagSplit.length > 1) {
        return findSupportedLocaleHelper(tagSplit[0], useDeprecatedLanguageTag);
    }
}
exports.findSupportedLocale = findSupportedLocale;
function findSupportedLocaleHelper(tag, useDeprecatedLanguageTag) {
    var tagLower = tag.toLocaleLowerCase();
    var deprecatedLanguageTag = useDeprecatedLanguageTag &&
        supportedLocales.find(function (l) { return tagLower === (l.deprecatedLanguageTag || "").toLocaleLowerCase(); });
    if (deprecatedLanguageTag) {
        return deprecatedLanguageTag;
    }
    var languageTag = supportedLocales.find(function (l) { return tagLower === l.languageTag.toLocaleLowerCase(); });
    if (languageTag) {
        return languageTag;
    }
    var localeTag = tagLower.indexOf("-") >= 0 &&
        supportedLocales.find(function (l) { return tagLower === l.localeTag.toLocaleLowerCase(); });
    if (localeTag) {
        return localeTag;
    }
}
exports.default = supportedLocales;
//# sourceMappingURL=supportedLocales.js.map